import {
  Box,
  Chip,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import { deletePurchaseOrderBills } from 'api/index';
import type { GetPurchaseOrders, PaymentPoDetails, PaymentScheduleForm, POModal } from 'api/types';
import AddCirlce from 'assets/images/add_circle.svg';
import DeleteIcon from 'assets/images/delete-icon.svg';
import InfoIcon from 'assets/images/info-icon.svg';
import type { DropdownOptionType } from 'components/inputs/Dropdown';
import CustomButton from 'components/NewLayout/Button';
import CustomDialog from 'components/NewLayout/Dialog';
import DropDown from 'components/NewLayout/Dropdown';
import CustomTextfield from 'components/NewLayout/Textfield';
import { useEffect, useReducer, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Validator from 'simple-react-validator';
import { getValidations } from 'utils/index';

export const StyledLegend = styled('div')(({ theme }) => ({
  fontSize: '16px',
  marginBottom: theme.spacing(1),
  width: 'max-content',
  background: '#fff',
  marginTop: '-29px !important',
  paddingLeft: '7px',
  paddingRight: '5px'
}));

export const paymentMethods: DropdownOptionType[] = [
  { id: 'ACH', value: 'ACH' },
  { id: 'Wire', value: 'Wire' },
  { id: 'Live Check', value: 'Live Check' },
  { id: 'Bill Pay', value: 'Bill Pay' },
  { id: 'Credit Card', value: 'Credit Card' }
];

export default function UpdatePaymentScheduleModal({
  paymentPoDetails,
  purchaseOrder,
  // POModalData,
  selectedVendorPayment,
  onClose,
  onComplete,
  onBackToEditPo
}: {
  paymentPoDetails?: PaymentPoDetails;
  purchaseOrder: GetPurchaseOrders;
  POModalData?: POModal[];
  selectedVendorPayment?: string;
  onClose: () => void;
  onComplete: (paymentSchedule: PaymentScheduleForm[]) => Promise<void>;
  onBackToEditPo?: () => void;
}) {
  const paymentScheduleObj = {
    index: 0,
    po_in_total: '',
    amount: undefined,
    due_date: '',
    payment_method: selectedVendorPayment,
    disabled: false
  };
  const [modalShow, setShowModal] = useState<boolean>(true);
  const [loader, setLoader] = useState<boolean>(false);
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentScheduleForm[]>([]);
  const [processedPaymentSchedules, setProcessedPaymentSchedules] = useState<PaymentScheduleForm[]>(
    []
  );
  const [deleteBillsList, setDeleteBillsList] = useState<number[]>([]);
  const [isWarningModalVisible, setWarningModalVisibility] = useState<boolean>(false);
  const validator = useRef(new Validator(getValidations()));
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const theme = useTheme();

  useEffect(() => {
    if (Array.isArray(purchaseOrder.paymentschedules)) {
      const initialPaymentData = purchaseOrder.paymentschedules.map((item, index) => {
        const dueDateString = item.due_date;
        let dueDate;

        if (dueDateString) {
          const parsedDate = new Date(dueDateString);
          if (!isNaN(parsedDate.getTime())) {
            dueDate = parsedDate;
          } else {
            dueDate = '';
          }
        } else {
          dueDate = '';
        }

        // Round percentage to whole number
        const percentageValue =
          ((item.amount != null ? Number(item.amount) : 0) /
            Number(paymentPoDetails?.total_amount?.toString())) *
          100;

        return {
          index,
          bill_id: item.bill_id,
          disabled: item.status == 'Paid' || item.status == 'Pending',
          due_date: purchaseOrder.purchase_date || '',
          po_in_total: Math.round(percentageValue).toString(), // Round to whole number
          amount: Number(item.amount),
          payment_method: item.payment_method,
          status: item.status
        };
      });

      setPaymentSchedule(initialPaymentData);

      const poTotal = Number(purchaseOrder.total_price || 0);
      const paymentTotal = Number(paymentPoDetails?.total_amount || 0);

      if (poTotal.toFixed(2) !== paymentTotal.toFixed(2)) {
        toast.success('YOU MUST UPDATE THE PAYMENT SCHEDULE FOR YOUR CHANGES TO BE APPLIED', {
          position: 'top-center',
          id: 'changes_in_amount'
        });
      }
    } else {
      setPaymentSchedule([]);
    }
  }, [purchaseOrder]);

  const handleChange = (event, index, field) => {
    const { value } = event.target;
    const updatedData = [...paymentSchedule];

    if (field === 'amount') {
      // Check if the value is a valid number
      const trimmedValue = value.trim();

      if (trimmedValue === '' || !isNaN(Number(trimmedValue))) {
        let numericValue = trimmedValue === '' ? 0 : Number(trimmedValue);

        // Calculate total amount already allocated excluding the current item
        const totalAllocatedAmount = paymentSchedule.reduce(
          (total, item, i) =>
            i !== index && !item.disabled ? total + (Number(item.amount) || 0) : total,
          0
        );

        // Calculate maximum available amount
        const maxAvailableAmount = paymentPoDetails?.total_amount
          ? Number(paymentPoDetails.total_amount) - totalAllocatedAmount
          : 0;

        // If entered amount exceeds available, adjust to maximum
        if (numericValue > maxAvailableAmount) {
          numericValue = Math.max(0, maxAvailableAmount);
          toast.error(`Adjusted to maximum available amount ($${numericValue.toFixed(2)})`, {
            position: 'top-center',
            id: 'max_amount_error'
          });
        }

        // Update amount with adjusted value
        updatedData[index] = {
          ...updatedData[index],
          amount: value
        };

        // Update percentage based on new amount
        if (paymentPoDetails?.total_amount) {
          const percentage = (Number(value) / Number(paymentPoDetails.total_amount)) * 100;
          updatedData[index].po_in_total = Math.round(percentage).toString(); // Round to whole number
        }
      } else {
        // Handle non-numeric input
        updatedData[index] = {
          ...updatedData[index],
          amount: value
        };
      }
    } else if (field === 'po_in_total') {
      // Force whole number input for percentages
      const trimmedValue = value.replace('%', '').trim();

      if (trimmedValue === '' || !isNaN(Number(trimmedValue))) {
        let numericValue = trimmedValue;

        // Calculate remaining percentage available (excluding the current item)
        const totalUsedPercentage = paymentSchedule.reduce(
          (total, item, i) =>
            i !== index && !item.disabled ? total + (Number(item.po_in_total) || 0) : total,
          0
        );

        const remainingPercentage = 100 - totalUsedPercentage;

        // If entered percentage exceeds remaining available, set to remaining available
        if (Number(trimmedValue) > remainingPercentage) {
          numericValue = Math.max(0, remainingPercentage).toFixed(2);
          toast.error(`Adjusted to maximum available percentage (${remainingPercentage}%)`, {
            position: 'top-center',
            id: 'max_percentage_error'
          });
        }
      }

      updatedData[index] = {
        ...updatedData[index],
        po_in_total: value
      };

      // Calculate amount based on percentage, but don't validate here
      if (trimmedValue && !isNaN(Number(trimmedValue)) && paymentPoDetails?.total_amount) {
        const amount = (Number(trimmedValue) / 100) * Number(paymentPoDetails.total_amount);
        updatedData[index].amount = Number(amount.toFixed(2));
      }
    } else {
      updatedData[index] = {
        ...updatedData[index],
        [field]: value
      };
    }

    setPaymentSchedule(updatedData);
  };

  // Validate percentage to ensure it's a whole number
  const validateWholeNumberPercentage = (value) => {
    if (value === '') return true;
    const number = Number(value);
    return !isNaN(number) && Number.isInteger(number);
  };

  const dataToInsert = (): PaymentScheduleForm[] => {
    const paymentSchedules: PaymentScheduleForm[] = paymentSchedule.map((d, index) => ({
      index,
      bill_id: d?.bill_id || null,
      amount: Number(d.amount),
      due_date: d.due_date,
      payment_method: d.payment_method,
      po_in_total: d.po_in_total,
      status: d?.status || 'Paid' // Keep existing status or set new ones to 'Paid'
    }));
    return paymentSchedules;
  };

  const submitForm = async () => {
    if (validator.current.allValid()) {
      // Validate that all percentages are whole numbers
      const invalidPercentages = paymentSchedule.filter(
        (item) => !item.disabled && !validateWholeNumberPercentage(item.po_in_total)
      );

      if (invalidPercentages.length > 0) {
        toast.error('Percentages must be whole numbers (no decimals)', {
          position: 'top-center',
          id: 'whole_number_error'
        });
        return;
      }

      // Calculate total percentage
      const totalPercentage = paymentSchedule.reduce(
        (total, item) => total + (item.disabled ? 0 : Number(item.po_in_total) || 0),
        0
      );

      // Check if total percentage exceeds 100%
      if (totalPercentage > 100) {
        toast.error('Total percentage allocation cannot exceed 100%', {
          position: 'top-center',
          id: 'percentage_exceed_error'
        });
        return;
      }

      // Check if any amount is less than 0.01
      const invalidAmounts = paymentSchedule.filter(
        (item) => !item.disabled && item.amount !== undefined && Number(item.amount) < 0.01
      );

      if (invalidAmounts.length > 0) {
        toast.error('Payment amounts cannot be less than $0.01', {
          position: 'top-center',
          id: 'less_than_minimum'
        });
        return;
      }

      const paymentScheduleSum = paymentSchedule
        .reduce((acc, item) => acc + (Number(item.amount) ?? 0), 0)
        .toFixed(2);

      if (paymentPoDetails?.total_amount !== undefined) {
        const totalAmount = Number(paymentPoDetails.total_amount).toFixed(2);

        if (
          Number(paymentScheduleSum) > Number(totalAmount) ||
          Number(paymentScheduleSum) < Number(totalAmount)
        ) {
          toast.error(`Total of Bill(s) must match $${totalAmount}`, {
            position: 'top-center'
          });
        } else {
          const data = dataToInsert();
          setPaymentSchedule(data);
          const hasInvalidDate = data.some((item) => item.due_date === '2099-01-01');
          if (hasInvalidDate) {
            setShowModal(false);
            setWarningModalVisibility(true);
          } else {
            await continueUpdatingPaymentSchedule();
          }
        }
      }
    } else {
      validator.current.showMessages();
      forceUpdate();
    }
  };

  const continueUpdatingPaymentSchedule = async () => {
    setLoader(true);
    if (deleteBillsList.length) {
      await handleDeleteBills();
    }

    setDeleteBillsList([]);
    const data = dataToInsert();
    await onComplete(data);
    setLoader(false);
    setShowModal(false);
    setWarningModalVisibility(false);
  };

  const handleAddPaymentSchedule = () => {
    const newIndex = paymentSchedule?.length;
    const newPaymentObj = { ...paymentScheduleObj, index: newIndex };
    setPaymentSchedule([...paymentSchedule, newPaymentObj]);
  };

  const handleDeletePaymentSchedule = (indexToDelete: number, billId: number) => {
    setPaymentSchedule((prevData) => prevData?.filter((item) => item.index !== indexToDelete));
    if (billId) {
      setDeleteBillsList((prevList) => [...(prevList || []), billId]);
    }
  };

  const handleDeleteBills = () => {
    const formattedBills = {
      billIds: deleteBillsList.map((id) => ({ id }))
    };
    return deletePurchaseOrderBills(formattedBills.billIds);
  };

  validator.current.purgeFields();

  return (
    <>
      <CustomDialog
        title="Update Payment Schedule"
        open={modalShow}
        maxWidth="md"
        closeDialog={() => onClose()}
        loading={loader}
        content={
          <>
            <Box className="payment-modal-table">
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Purchase Order Number</TableCell>
                      <TableCell>Total PO Amount</TableCell>
                      <TableCell>Total Paid</TableCell>
                      <TableCell>Total Remaining</TableCell>
                      <TableCell>Total Scheduled</TableCell>
                      <TableCell>Vendor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow
                      key={'payment-moda-table'}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>{paymentPoDetails?.po_number || '00000'}</TableCell>
                      <TableCell component="th" scope="row">
                        {'$' + paymentPoDetails?.total_amount.toFixed(2)}
                      </TableCell>
                      <TableCell>{'$' + (paymentPoDetails?.paid_amount.toFixed(2) || 0)}</TableCell>
                      <TableCell>
                        {'$' + paymentPoDetails?.remaining_amount.toFixed(2) || 0}
                      </TableCell>
                      <TableCell>
                        {'$' + (paymentPoDetails?.scheduled_amount.toFixed(2) || 0)}
                      </TableCell>
                      <TableCell>{paymentPoDetails?.vendor_name || 'No Vendor Name'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Auto-paid on creation tag and helper text */}
            <Box sx={{ mb: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip
                  label="Auto-paid on creation"
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: '#e3f2fd',
                    borderColor: '#1976d2',
                    color: '#1565c0'
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.875rem',
                  fontStyle: 'italic',
                  lineHeight: 1.4
                }}>
                Scheduled bills will automatically sync to QuickBooks immediately upon creation and
                will be marked as paid in the system.
              </Typography>
            </Box>

            <Typography component="div" sx={{ marginBottom: '30px !important' }}>
              Create payment schedule by adding the following fields.
            </Typography>
            {paymentSchedule?.map((d, i) => (
              <Box
                key={i}
                component="form"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  border: `1px solid #dadada`,
                  borderRadius: 1,
                  p: 2,
                  mb: 3
                }}>
                <fieldset style={{ borderColor: theme.palette.divider }}>
                  <StyledLegend>
                    Payment Schedule
                    <div
                      onClick={() =>
                        !d.disabled &&
                        handleDeletePaymentSchedule(Number(d.index), Number(d?.bill_id))
                      }
                      style={{
                        cursor: d.disabled ? 'not-allowed' : 'pointer',
                        display: 'inline-block',
                        margin: '0 0 6px 12px'
                      }}>
                      <img
                        className="img-fluid link-icon"
                        src={DeleteIcon}
                        alt="Delete Icon"
                        style={{
                          pointerEvents: d.disabled ? 'none' : 'auto'
                        }}
                      />
                    </div>
                  </StyledLegend>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <CustomTextfield
                        type="number"
                        min="0"
                        labelText="Amount"
                        name={`paymentSchedule.${i}.amount`}
                        value={d.amount?.toString() ?? ''}
                        onChange={(event) => handleChange(event, i, 'amount')}
                        isRequired
                        maxLength={250}
                        validator={validator}
                        size="medium"
                        disabled={d.disabled}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextfield
                        labelText="PO Total in % (whole numbers only)"
                        name={`paymentSchedule.${i}.po_in_total`}
                        value={d.po_in_total}
                        onChange={(event) => handleChange(event, i, 'po_in_total')}
                        isRequired={false}
                        disabled={d.disabled}
                        size="medium"
                        endAdornment="%"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Only whole numbers (0-100) are allowed"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CustomTextfield
                        validator={validator}
                        labelText="Purchase Date"
                        name={`paymentSchedule.${i}.due_date`}
                        value={d.due_date}
                        isRequired={true}
                        onChange={(event) => handleChange(event, i, 'due_date')}
                        type="date"
                        size="medium"
                        // disabled={d.disabled}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DropDown
                        label="Payment Method"
                        inputName={`paymentSchedule.${i}.payment_method`}
                        optionText="Select payment method"
                        value={d.payment_method || ''}
                        onChange={(event) => handleChange(event, i, 'payment_method')}
                        options={paymentMethods}
                        validator={validator}
                        disabled={d.disabled}
                        required
                      />
                    </Grid>
                  </Grid>
                </fieldset>
              </Box>
            ))}
            <Typography
              component="div"
              sx={{
                width: '100%',
                textAlign: 'center',
                marginTop: '30px !important'
              }}>
              <CustomButton
                onClick={handleAddPaymentSchedule}
                type="info"
                disabled={Number(paymentPoDetails?.remaining_amount) <= 0}
                label={
                  <>
                    <img src={AddCirlce} style={{ marginRight: '10px' }} /> Add another payment
                  </>
                }
              />
            </Typography>
          </>
        }
        actions={
          <Box
            sx={{
              display: 'flex',
              justifyContent: onBackToEditPo ? 'space-between' : 'flex-end',
              width: '100%'
            }}>
            {onBackToEditPo && (
              <CustomButton
                disabled={loader}
                onClick={() => onBackToEditPo()}
                variant="outlined"
                label="Back to PO"
              />
            )}
            <Box>
              <CustomButton
                disabled={loader}
                onClick={() => onClose()}
                variant="outlined"
                label="Discard"
              />
              <CustomButton
                disabled={!paymentSchedule?.length || loader}
                onClick={() => submitForm()}
                label="Update Payment Schedule"
              />
            </Box>
          </Box>
        }
      />

      <CustomDialog
        title={
          <Typography component="div" sx={{ color: '#335d87' }}>
            <img src={InfoIcon} width="30px" />
            Warning
          </Typography>
        }
        open={isWarningModalVisible}
        maxWidth="xs"
        closeDialog={() => {
          setWarningModalVisibility(false);
          setShowModal(true);
        }}
        loading={loader}
        content={
          <Typography>
            This payment schedule was created by the system. The due date is still set to
            01-01-2099. Are you sure you want to proceed with changing this status?
          </Typography>
        }
        actions={
          <>
            <CustomButton
              onClick={() => {
                setWarningModalVisibility(false);
                setShowModal(true);
              }}
              variant="outlined"
              label="Discard"
            />
            <CustomButton onClick={() => continueUpdatingPaymentSchedule()} label="Update Bill" />
          </>
        }
      />
    </>
  );
}
